// src/utils/avatar-url.ts
/**
 * Single source of truth for avatar URL normalization.
 *
 * Accepts:
 * - absolute URLs: http(s)://...
 * - blob URLs: blob:...
 * - data URLs: data:image/...
 * - root-relative URLs: /avatars/...
 *
 * Rejects:
 * - empty strings / whitespace
 * - literal "null"/"undefined"
 * - non-string values
 * - backend avatar proxy URLs (/profiles/avatar/...) so they are never used as <img src>
 */
export function normalizeAvatarUrl(v: unknown): string | undefined {
    if (typeof v !== "string") return undefined;

    const s = v.trim();
    if (!s) return undefined;

    const lowered = s.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return undefined;

    // ✅ Explicitly reject backend proxy avatar URLs so they never end up as <img src>
    // This covers both absolute and root-relative forms:
    // - https://api.example.com/profiles/avatar/<id>?t=...
    // - /profiles/avatar/<id>?t=...
    if (lowered.includes("/profiles/avatar/")) return undefined;

    // allow common safe schemes and root-relative
    if (
        s.startsWith("http://") ||
        s.startsWith("https://") ||
        s.startsWith("blob:") ||
        s.startsWith("data:image/") ||
        s.startsWith("/")
    ) {
        return s;
    }

    // If your backend returns something like "profiles/avatar/123", you can choose
    // to support it. For now we treat unknown schemes/relative non-root paths as invalid.
    return undefined;
}
